import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useReducedMotion } from './motion'
import './drawer-legacy.css'

// The right-hand panel for detail that would crowd a screen: an integration's
// setup, "About this stage", a row's preview.
//
// # WHAT IT PROMISES A KEYBOARD USER
//
// It is a modal dialog in every sense a screen reader or a keyboard can
// detect. Opening it moves focus to the first thing that can take focus inside
// it; Tab and Shift+Tab cycle inside it and never reach the page behind;
// Escape and the scrim close it; closing it returns focus to whatever opened
// it, so the reader is back where they were. It is drawn in a portal on the
// body so no panel with overflow hidden or a transform can clip or displace it.
//
// It slides 24px and fades over 240 ms, and stays in the document for those
// 240 ms after it is told to close so the exit is seen. With reduced motion it
// simply appears and disappears.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getClientRects().length > 0)
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  className,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  // A variant on the root, for the one caller that wants the same dialog in
  // the middle of the screen rather than at its edge (the Reassess pop-up).
  className?: string
}) {
  const reduced = useReducedMotion()
  const [mounted, setMounted] = useState(open)
  const [shown, setShown] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const closeRef = useRef(onClose)
  const titleId = useId()

  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  // Mount on open; on close, fade out and then leave the document.
  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    setShown(false)
    const back = returnTo.current
    returnTo.current = null
    if (back && document.contains(back)) back.focus()
    const t = window.setTimeout(() => setMounted(false), reduced ? 0 : 240)
    return () => window.clearTimeout(t)
  }, [open, reduced])

  // Once the panel is in the document: remember where focus was, move it in,
  // and start the entrance on the next frame.
  useEffect(() => {
    if (!open || !mounted || !panel.current) return
    const active = document.activeElement
    if (active instanceof HTMLElement && !panel.current.contains(active)) returnTo.current = active
    const first = focusables(panel.current)[0] ?? panel.current
    first.focus()
    const raf = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(raf)
  }, [open, mounted])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      const root = panel.current
      if (e.key !== 'Tab' || !root) return
      const list = focusables(root)
      const first = list[0]
      const last = list[list.length - 1]
      const active = document.activeElement
      if (!first || !last) {
        e.preventDefault()
        root.focus()
        return
      }
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!mounted) return null

  return createPortal(
    <div className={'kit-drawer-root' + (className ? ' ' + className : '') + (shown ? ' kit-on' : '')}>
      <div className="kit-scrim" onClick={() => closeRef.current()} aria-hidden="true" />
      <div ref={panel} className="kit-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="kit-drawer-head">
          <h2 id={titleId} className="kit-drawer-title">
            {title}
          </h2>
          <button type="button" className="kit-drawer-close" onClick={() => closeRef.current()}>
            Close
          </button>
        </header>
        <div className="kit-drawer-body">{children}</div>
        {footer && <footer className="kit-drawer-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}
